/**
 * D346 (a)/(b) — the round avatar the address book's rows and the collect step's picked rows
 * both wear. A P7 path (`screens/invite/`), so no `web-ownership-map.ts` row moves.
 *
 * **Initials, never `name[0]`.** A display name is a string of code units, not of letters — an
 * emoji, a combining mark or a Hebrew name each disagree with `[0]` in a different way — so the
 * first grapheme is read with `Intl.Segmenter` (the first code point where a runtime lacks it —
 * {@link graphemeSegmenter}), and only a grapheme that contains a Unicode letter
 * (`\p{L}`) counts. The first *two* such graphemes, from the first two words that start with one,
 * become the initials; Latin is upper-cased (Hebrew has no case to normalize). A name with no
 * letter in it at all — or no name — draws the `user` glyph instead of blank space.
 *
 * **Colour is a stable hash into the fifteen personalization swatches, and nothing else.** They
 * are `VIEW_COLORS` (`components/icons/viewVocabulary.ts`) — the same fifteen names Home's tile
 * and Gate Detail's identity chip already read `--pc-*`/`--pc-*-wash` from, so this draws no
 * sixteenth swatch and no new token. Status colours (`--green`/`--amber`/`--red`) and the four
 * gate-type accents are refused: each already carries a meaning on another screen, and a contact
 * avatar tinted the same way as an alarm or a gate type would borrow that meaning by accident.
 * 🔸 The hash itself — a stable but otherwise arbitrary function of the display name (the first
 * number when there is none) — is the author's reading and vetoable; what must not change is the
 * fifteen-name universe it draws from.
 */
import { useMemo } from 'react'
import { Icon, VIEW_COLORS, personalizationColor, personalizationWash } from '../../components'

const LETTER = /\p{L}/u

/**
 * **Built on first use and guarded, never at module scope.** `Intl.Segmenter` is missing from
 * Firefox before 125 (ESR 115 among them), and a constructor call at import would throw while
 * `InviteScreen` loads — taking the whole screen down, manual entry included, over an avatar.
 * Where it is missing the fallback is the first **code point** (`Array.from`), not `[0]`: it
 * splits a combining mark from its base, but it never halves a surrogate pair, and the `\p{L}`
 * test below still decides whether the result counts as a letter.
 */
let segmenter: Intl.Segmenter | null | undefined

function graphemeSegmenter(): Intl.Segmenter | null {
  if (segmenter === undefined) {
    segmenter =
      typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null
  }
  return segmenter
}

/** The first grapheme of `text` (see above for the fallback), or `undefined` for an empty string. */
export function firstGrapheme(text: string): string | undefined {
  const segments = graphemeSegmenter()
  if (segments === null) return Array.from(text)[0]
  for (const { segment } of segments.segment(text)) return segment
  return undefined
}

/** Whether a grapheme contains a Unicode letter — the one test the avatar and §4.3's headers share. */
export function isLetterGrapheme(grapheme: string): boolean {
  return LETTER.test(grapheme)
}

/**
 * A word's first grapheme, but only when that grapheme is itself a letter — a word that does not
 * *start* with one (a parenthesised aside, an emoji, a bare number) is skipped whole rather than
 * searched for a letter further in, which is what makes it not count as one of "the first two
 * words that start with a letter."
 */
function firstGraphemeIfLetter(word: string): string | undefined {
  const first = firstGrapheme(word)
  return first !== undefined && isLetterGrapheme(first) ? first : undefined
}

/**
 * The first letter-grapheme of the first two *words* that start with one — not just the first two
 * letter-graphemes of the string, so "Dana Levi" reads as D+L rather than D+a. A single-word name
 * (or a contact filed under one word) still yields one initial.
 */
function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter((word) => word.length > 0)
  const letters: string[] = []
  for (const word of words) {
    const first = firstGraphemeIfLetter(word)
    if (first !== undefined) letters.push(first)
    if (letters.length === 2) break
  }
  return letters.map((letter) => letter.toLocaleUpperCase()).join('')
}

/** A stable, otherwise arbitrary hash into the fifteen swatches — same name, same colour, always. */
function swatchFor(seed: string): (typeof VIEW_COLORS)[number] {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.codePointAt(i)!) | 0
  const index = Math.abs(hash) % VIEW_COLORS.length
  return VIEW_COLORS[index]!
}

export function ContactAvatar({
  name,
  phone,
}: {
  readonly name?: string
  readonly phone: string
}): JSX.Element {
  const initials = useMemo(() => (name === undefined ? '' : initialsOf(name)), [name])
  const swatch = useMemo(() => swatchFor(name ?? phone), [name, phone])

  return (
    <span className="contact-avatar" aria-hidden="true">
      <span
        className="contact-avatar-glyph"
        style={{ color: personalizationColor(swatch), background: personalizationWash(swatch) }}
      >
        {initials === '' ? <Icon name="user" size={16} /> : initials}
      </span>
    </span>
  )
}
